/**
 * authErrorPolicy.ts
 * ------------------
 * Pure decision matrix mapping (messageCode, messageDescription) -> AuthAction.
 * No side effects, so the entire authentication contract can be unit-tested.
 * This table is the single source of truth for how the client reacts.
 */
import {
  AUTH_MESSAGE_DESCRIPTIONS,
  KNOWN_500_DESCRIPTIONS,
} from '@constants/authErrorConstants';

export type AuthAction =
  | 'FIX_REQUEST' //         400 - malformed/invalid request; no auto-retry
  | 'REFRESH_TOKEN' //       401 TOKEN_EXPIRED - silent refresh + retry original
  | 'LOGIN_REQUIRED' //      401 others / 400 corrupted token - clear token, force login
  | 'RETRY_LATER' //         503 - capped backoff retry, keep session
  | 'PLATFORM_ERROR' //      500 known auth-internal - retryable platform toast
  | 'SERVICE_UNAVAILABLE' // 500 unknown (db etc.) - service-unavailable toast
  | 'NONE';

export function decideAuthAction(code: string, description: string): AuthAction {
  switch (code) {
    case '400':
      // A corrupted token must be cleared and re-authenticated; other 400s
      // (e.g. AUTH_HEADER_INVALID) are dev-facing and handled silently.
      return description === AUTH_MESSAGE_DESCRIPTIONS.AUTH_TOKEN_MALFORMED
        ? 'LOGIN_REQUIRED'
        : 'FIX_REQUEST';
    case '401':
      return description === AUTH_MESSAGE_DESCRIPTIONS.TOKEN_EXPIRED
        ? 'REFRESH_TOKEN'
        : 'LOGIN_REQUIRED';
    case '503':
      return 'RETRY_LATER';
    case '500':
      return KNOWN_500_DESCRIPTIONS.includes(description) ? 'PLATFORM_ERROR' : 'SERVICE_UNAVAILABLE';
    default:
      return 'NONE';
  }
}
